<script setup>
// Zadanie: treść, dzień w planerze (albo „Do rozplanowania" — bez dnia), opcjonalna godzina
// z przypomnieniem, powtarzanie i opcjonalny projekt. Bez projektu zadanie jest samodzielne.
import { computed } from 'vue';
import WhenFields from '@/components/v2/add/when-fields.vue';
import SelectField from '@/components/v2/select-field.vue';
import { REPEAT_OPTIONS, REMINDER_OPTIONS, WEEKDAYS, repeatLabel } from '@/composables/usePlaner.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    draft: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    checklists: { type: Array, default: () => [] },
});

const { t } = useI18n();

const toggleDay = (iso) => {
    const days = new Set(props.draft.repeatDays ?? []);
    if (days.has(iso)) days.delete(iso);
    else days.add(iso);
    props.draft.repeatDays = [...days].sort();
};

const setRepeat = (freq) => {
    props.draft.repeat = freq;
    // co tydzień: domyślnie w dzień tygodnia zadania
    if (freq === 'weekly' && !(props.draft.repeatDays ?? []).length) props.draft.repeatDays = [((props.draft.day.getDay() + 6) % 7) + 1];
};

// Do wyboru tylko niezakończone projekty — plus ten, w którym zadanie już jest.
const projects = computed(() => props.checklists
    .filter(c => !c.finished_at || c.uuid === props.draft.savedChecklistUuid)
    .map(c => ({ value: c.uuid, label: c.title || t('entry.task.untitledProject') })));

const summary = computed(() => repeatLabel(props.draft.repeat === 'none' ? null : {
    freq: props.draft.repeat, interval: Number(props.draft.repeatInterval) || 1, days: props.draft.repeatDays,
}));

const chip = 'h-8 px-3 rounded-full border text-[12.5px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]';
const on = 'border-[#211E1A] bg-[#211E1A] text-[#FBF9F4]';
const off = 'border-[#E4DDD1] text-[#4A443C] hover:border-[#211E1A]';
</script>

<template>
    <div>
        <input
            v-model="draft.text"
            type="text"
            class="v2-freetext mb-1"
            :placeholder="t('entry.task.textPlaceholder')"
            :aria-invalid="!!errors.text"
        />
        <p v-if="errors.text" class="text-[11.5px] text-[#B5643C] mb-2">{{ errors.text }}</p>

        <div class="border-t border-[#EFE9DF] pt-4 mt-4 mb-4">
            <WhenFields v-if="!draft.noDay" :draft="draft" :errors="errors" time-optional />
            <p v-else class="text-[12.5px] text-[#6B655C]">{{ t('entry.task.noDayInfo') }}</p>
            <label class="inline-flex items-center gap-2 mt-3 text-[12.5px] text-[#4A443C] cursor-pointer">
                <input v-model="draft.noDay" type="checkbox" class="w-[15px] h-[15px] accent-[#5B8C6E]" />
                {{ t('entry.task.noDayToggle') }}
            </label>
        </div>

        <!-- Przypomnienie ma sens tylko przy godzinie -->
        <div v-if="draft.withTime && !draft.noDay" class="mb-4">
            <span class="v2-label">{{ t('entry.task.reminder') }}</span>
            <div class="flex flex-wrap gap-1.5">
                <button
                    v-for="o in REMINDER_OPTIONS"
                    :key="String(o.value)"
                    type="button"
                    :class="[chip, draft.remindBefore === o.value ? on : off]"
                    :aria-pressed="draft.remindBefore === o.value"
                    @click="draft.remindBefore = o.value"
                >{{ o.label }}</button>
            </div>
        </div>

        <div v-if="!draft.noDay" class="mb-4">
            <span class="v2-label">{{ t('entry.task.repeat') }}</span>
            <div class="flex flex-wrap gap-1.5">
                <button
                    v-for="o in REPEAT_OPTIONS"
                    :key="o.value"
                    type="button"
                    :class="[chip, draft.repeat === o.value ? on : off]"
                    :aria-pressed="draft.repeat === o.value"
                    @click="setRepeat(o.value)"
                >{{ o.label }}</button>
            </div>

            <div v-if="draft.repeat === 'weekly'" class="flex flex-wrap gap-1.5 mt-2.5" role="group" :aria-label="t('entry.task.weekdays')">
                <button
                    v-for="d in WEEKDAYS"
                    :key="d.iso"
                    type="button"
                    :class="[chip, 'w-10 px-0', (draft.repeatDays ?? []).includes(d.iso) ? on : off]"
                    :aria-pressed="(draft.repeatDays ?? []).includes(d.iso)"
                    :aria-label="d.long"
                    @click="toggleDay(d.iso)"
                >{{ d.short }}</button>
            </div>

            <div v-if="['daily', 'weekly', 'monthly'].includes(draft.repeat)" class="flex items-center gap-2 mt-2.5 text-[12.5px] text-[#4A443C]">
                <span>{{ t('entry.task.every') }}</span>
                <input v-model.number="draft.repeatInterval" type="number" min="1" max="52" class="v2-input w-[64px] h-8 text-center" :aria-label="t('entry.task.interval')" />
                <span>{{ t(`entry.task.intervalUnit.${draft.repeat}`, Number(draft.repeatInterval) || 1) }}</span>
            </div>

            <p v-if="summary" class="text-[11.5px] text-[#8A8378] mt-2">{{ t('entry.task.repeatSummary', { summary }) }}</p>
            <p v-if="errors.repeat" class="text-[11.5px] text-[#B5643C] mt-1">{{ errors.repeat }}</p>
        </div>

        <div>
            <label class="v2-label" for="v2-checklist">{{ t('entry.task.project') }}</label>
            <SelectField
                id="v2-checklist"
                v-model="draft.checklistUuid"
                :options="projects"
                :empty-label="t('entry.task.noProject')"
                :search-placeholder="t('entry.task.searchProject')"
            />
        </div>
    </div>
</template>
